import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

// Root route stays eager so the primary funnel renders without a Suspense fallback.
import GetGrowthScorePage from "./pages/GetGrowthScorePage.tsx";

// Everything else is code-split — keeps the initial bundle small and the homepage fast.
const Index = lazy(() => import("./pages/Index.tsx"));
const ApplyPage = lazy(() => import("./pages/ApplyPage.tsx"));
const ThankYouPage = lazy(() => import("./pages/ThankYouPage.tsx"));
const FbAdsPage = lazy(() => import("./pages/FbAdsPage.tsx"));
const FbAdsV2Page = lazy(() => import("./pages/FbAdsV2Page.tsx"));
const FbAdsV3Page = lazy(() => import("./pages/FbAdsV3Page.tsx"));
const FbAdsV3LaunchPage = lazy(() => import("./pages/FbAdsV3LaunchPage.tsx"));
const GcAdsPage = lazy(() => import("./pages/GcAdsPage.tsx"));
const QuickScorePage = lazy(() => import("./pages/QuickScorePage.tsx"));
const GetGrowthScoreThankYouPage = lazy(() => import("./pages/GetGrowthScoreThankYouPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const DashboardPage = lazy(() => import("./pages/DashboardPage.tsx"));
const DashboardErrorBoundary = lazy(() => import("./components/dashboard/DashboardErrorBoundary.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-6 w-6 rounded-full border-2 border-muted border-t-primary animate-spin" aria-label="Loading" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<GetGrowthScorePage />} />
            <Route path="/GetGrowthScore" element={<GetGrowthScorePage />} />
            <Route path="/GetGrowthScore/thank-you" element={<GetGrowthScoreThankYouPage />} />
            <Route path="/thank-you" element={<GetGrowthScoreThankYouPage />} />
            <Route path="/home" element={<Index />} />
            <Route path="/apply" element={<ApplyPage />} />
            <Route path="/apply/thank-you" element={<ThankYouPage />} />
            <Route path="/fb-ads" element={<FbAdsPage />} />
            <Route path="/fb-ads-v2" element={<FbAdsV2Page />} />
            <Route path="/fb-ads-v3" element={<FbAdsV3Page />} />
            <Route path="/fb-ads-v3-launch" element={<FbAdsV3LaunchPage />} />
            <Route path="/gc-ads" element={<GcAdsPage />} />
            <Route path="/quick-score" element={<QuickScorePage />} />
            <Route
              path="/dashboard"
              element={
                <DashboardErrorBoundary>
                  <DashboardPage />
                </DashboardErrorBoundary>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
